import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSpinner } from 'react-icons/fa';
import { VariableSelectionSectionProps } from './types';

/**
 * VariableSelectionSection sub-component
 * Reusable checkbox list for selecting variables
 */
export const VariableSelectionSection: React.FC<VariableSelectionSectionProps> = memo(({
  title,
  variables,
  onVariableChange,
  emptyMessage,
  keyPrefix,
  isLoading = false
}) => {
  const { t: _t } = useTranslation('training');

  return (
    <div>
      {title && (
        <h4 className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">{title}</h4>
      )}
      <div className="max-h-48 overflow-y-auto rounded-lg border border-border bg-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <FaSpinner className="animate-spin text-primary text-sm" />
            <span className="ml-2 text-xs text-muted-foreground">Loading...</span>
          </div>
        ) : variables.length === 0 ? (
          <div className="flex items-center justify-center py-6">
            <p className="text-xs text-muted-foreground italic">{emptyMessage}</p>
          </div>
        ) : (
          <div className="p-1.5">
            {variables.map((variable, index) => (
              <label
                key={`${keyPrefix}-${variable.name}`}
                className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm cursor-pointer hover:bg-muted transition-colors duration-100"
              >
                <input
                  type="checkbox"
                  checked={variable.selected}
                  onChange={(e) => onVariableChange(index, e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-input text-primary focus:ring-ring/30 focus:ring-offset-0 cursor-pointer"
                />
                <span className={`truncate text-sm ${variable.selected ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                  {variable.name}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

VariableSelectionSection.displayName = 'VariableSelectionSection';

export default VariableSelectionSection;
